import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { 
  CreditCard, 
  Check, 
  Loader2, 
  Crown, 
  Users, 
  Sparkles,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function BillingPage() {
  const { t } = useLanguage();
  const { company } = useAuth();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [processingPlan, setProcessingPlan] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadPlans();
    
    // Check if returning from Stripe
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const loadPlans = async () => {
    try {
      const response = await api.get('/billing/plans');
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Failed to load plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const response = await api.get(`/billing/status/${sessionId}`);
        if (cancelled) return;
        
        if (response.data.payment_status === 'paid') {
          toast.success(t('billing.paymentSuccess'));
          window.location.href = '/billing';
          return;
        } else if (response.data.status === 'expired') {
          toast.error(t('billing.sessionExpired'));
          setCheckingPayment(false);
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          toast.info(t('billing.checkEmail'));
          setCheckingPayment(false);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error checking payment:', error);
          setCheckingPayment(false);
        }
      }
    };

    poll();
    return () => { cancelled = true; };
  };

  const handleUpgrade = async (planId) => {
    setProcessingPlan(planId);
    try {
      const originUrl = window.location.origin;
      const response = await api.post('/billing/checkout', null, {
        params: { plan: planId, origin_url: originUrl }
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(t('billing.checkoutError'));
      setProcessingPlan(null);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'starter': return <Users className="h-6 w-6" />;
      case 'business': return <Crown className="h-6 w-6" />;
      case 'enterprise': return <Sparkles className="h-6 w-6" />;
      default: return <Users className="h-6 w-6" />;
    }
  };

  const getPlanColor = (planId) => {
    switch (planId) {
      case 'starter': return 'from-blue-500 to-cyan-500';
      case 'business': return 'from-violet-500 to-purple-500';
      case 'enterprise': return 'from-amber-500 to-orange-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-wp-light" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="billing-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white font-heading">{t('billing.title')}</h1>
        <p className="text-wp-light/60 mt-2">{t('billing.subtitle')}</p>
      </div>

      {/* Current Plan */}
      {company && (
        <div className="glass-card rounded-2xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">{t('billing.currentPlan')}</h2>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-white capitalize">
                {company.subscription_plan === 'trial' ? t('billing.trialPeriod') : company.subscription_plan}
              </p>
              <p className="text-wp-light/60">
                {company.subscription_status === 'trial' && company.trial_ends_at && (
                  <>{t('billing.expires')}: {format(new Date(company.trial_ends_at), 'dd.MM.yyyy')}</>
                )}
                {company.subscription_status === 'active' && t('billing.active')}
              </p>
            </div>
            <div className="text-right">
              <p className="text-wp-light/60">{t('billing.employeeLimit')}</p>
              <p className="text-2xl font-bold text-white">
                {company.employee_count} / {company.max_employees}
              </p>
            </div>
          </div>
          
          {company.subscription_status === 'trial' && (
            <div className="mt-4 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-amber-400 shrink-0" />
              <p className="text-amber-200 text-sm">
                {t('billing.trialWarning')}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Checking Payment */}
      {checkingPayment && (
        <div className="glass-card rounded-2xl p-6 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-wp-light mx-auto mb-4" />
          <p className="text-white">{t('billing.checkingPayment')}</p>
        </div>
      )}

      {/* Plans */}
      <div className="grid md:grid-cols-3 gap-6">
        {plans.map((plan) => {
          const isCurrentPlan = company?.subscription_plan === plan.id;
          const isProcessing = processingPlan === plan.id;
          
          return (
            <div 
              key={plan.id}
              className={`glass-card rounded-2xl p-6 relative overflow-hidden ${
                isCurrentPlan ? 'ring-2 ring-indigo-500' : ''
              }`}
              data-testid={`plan-${plan.id}`}
            >
              {/* Background gradient */}
              <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${getPlanColor(plan.id)} opacity-10 blur-2xl`} />
              
              {/* Plan badge */}
              {isCurrentPlan && (
                <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-wp-medium/20 text-wp-lightest text-xs font-medium">
                  {t('billing.currentPlanBadge')}
                </div>
              )}
              
              {/* Icon */}
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getPlanColor(plan.id)} flex items-center justify-center text-white mb-4`}>
                {getPlanIcon(plan.id)}
              </div>
              
              {/* Plan name */}
              <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
              
              {/* Price */}
              <div className="mb-6">
                <span className="text-4xl font-bold text-white">€{plan.price}</span>
                <span className="text-wp-light/60">{t('billing.perMonth')}</span>
              </div>
              
              {/* Features */}
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-2 text-white/80">
                  <Check className="h-5 w-5 text-emerald-400" />
                  {t('billing.upToEmployees').replace('{count}', plan.max_employees)}
                </li>
                <li className="flex items-center gap-2 text-white/80">
                  <Check className="h-5 w-5 text-emerald-400" />
                  {t('billing.allFeatures')}
                </li>
                <li className="flex items-center gap-2 text-white/80">
                  <Check className="h-5 w-5 text-emerald-400" />
                  {t('billing.emailSupport')}
                </li>
                {plan.id === 'enterprise' && (
                  <li className="flex items-center gap-2 text-white/80">
                    <Check className="h-5 w-5 text-emerald-400" />
                    {t('billing.prioritySupport')}
                  </li>
                )}
              </ul>
              
              {/* Button */}
              <Button
                onClick={() => handleUpgrade(plan.id)}
                disabled={isCurrentPlan || isProcessing || checkingPayment}
                className={`w-full ${
                  isCurrentPlan 
                    ? 'bg-wp-dark/50 text-wp-light/50 cursor-not-allowed' 
                    : `bg-gradient-to-r ${getPlanColor(plan.id)} hover:opacity-90`
                }`}
                data-testid={`upgrade-${plan.id}`}
              >
                {isProcessing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : isCurrentPlan ? (
                  t('billing.currentPlanBtn')
                ) : (
                  <>
                    <CreditCard className="h-4 w-4 mr-2" />
                    {t('billing.select')}
                  </>
                )}
              </Button>
            </div>
          );
        })}
      </div>

      {/* Info */}
      <div className="glass-card rounded-xl p-4 text-center">
        <p className="text-wp-light/40 text-sm">
          {t('billing.securePayment')}
        </p>
      </div>
    </div>
  );
}
